import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { StakingOption } from "../types";
import { useAuthStore } from "../store/authStore";

const API_URL = import.meta.env.VITE_API_URL || "http://64.23.240.10/api/v1";
const BASE_API_URL = API_URL.replace("/v1", "");

interface CompareContextType {
  compareList: StakingOption[];
  addToCompare: (option: StakingOption) => boolean;
  removeFromCompare: (optionId: string) => void;
  clearCompare: () => void;
  isInCompare: (optionId: string) => boolean;
  maxCompare: number;
  limitReached: boolean;
  dismissLimitReached: () => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

export function CompareProvider({ children }: { children: ReactNode; maxCompare?: number }) {
  const [compareList, setCompareList] = useState<StakingOption[]>([]);
  const [limitReached, setLimitReached] = useState(false);
  const { isAuthenticated, user } = useAuthStore();

  const tier = user?.subscriptionTier || "free";
  const maxCompare = !isAuthenticated ? 2 : tier === "free" ? 2 : 5;

  const addToCompare = useCallback(
    (option: StakingOption): boolean => {
      if (compareList.length >= maxCompare) {
        setLimitReached(true);
        if (isAuthenticated && tier === "free") {
          const { token } = useAuthStore.getState();
          fetch(BASE_API_URL + "/free-tier/limit-hit", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
            body: JSON.stringify({ feature: "comparison" })
          }).catch(() => {});
        }
        return false;
      }
      if (!compareList.find((o) => o.id === option.id)) {
        setCompareList([...compareList, option]);
      }
      return true;
    },
    [compareList, maxCompare]
  );

  const removeFromCompare = useCallback(
    (optionId: string) => {
      setCompareList(compareList.filter((o) => o.id !== optionId));
    },
    [compareList]
  );

  const clearCompare = useCallback(() => {
    setCompareList([]);
    setLimitReached(false);
  }, []);

  const isInCompare = useCallback(
    (optionId: string) => {
      return compareList.some((o) => o.id === optionId);
    },
    [compareList]
  );

  const dismissLimitReached = useCallback(() => {
    setLimitReached(false);
  }, []);

  return (
    <CompareContext.Provider
      value={{
        compareList,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isInCompare,
        maxCompare,
        limitReached,
        dismissLimitReached,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within CompareProvider");
  }
  return context;
}
